// HowItWorks.js — landing page process section.
// Answers the "how does this actually work?" question the homepage
// previously left unanswered (hero + testimonials only). Steps describe
// the real service flow already referenced in AboutUs.js's comparison
// table (real-time app tracking, visa support).
import React from 'react';
// See HomeStats.js for why this uses LazyMotion + domAnimation (which
// covers whileInView) instead of importing the full `motion` namespace.
import { LazyMotion, domAnimation, m } from 'framer-motion';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import './HowItWorks.css';

const STEPS = [
  {
    number: '01',
    title: 'Free Consultation',
    description: 'Tell us your goals, budget, and preferred country. We map out a realistic path in one session.',
  },
  {
    number: '02',
    title: 'Application & Documents',
    description: 'We prepare and submit your university application, with real-time tracking so you always know where things stand.',
  },
  {
    number: '03',
    title: 'Your Offer',
    description: 'We apply to recognised partner universities that fit your grades, and explain every offer letter before you accept it.',
  },
  {
    number: '04',
    title: 'Visa & Travel Support',
    description: 'From visa paperwork to pre-departure prep, our team stays with you through to arrival on campus.',
  },
];

const HowItWorks = () => {
  const reducedMotion = usePrefersReducedMotion();

  return (
    <section className="how-it-works-section">
      <div className="how-it-works-container">
        <div className="how-it-works-header">
          <h2 className="how-it-works-title">How It Works</h2>
          <p className="how-it-works-subtitle">Four steps from first conversation to arrival on campus.</p>
        </div>

        <LazyMotion features={domAnimation} strict>
          <div className="how-it-works-grid">
            {/* Opacity is never part of this animation — cards are always
                fully visible (to crawlers, print, screenshot tools, and
                anyone who never scrolls them into view); only position
                animates, and reduced-motion skips that too. */}
            {STEPS.map((step, index) => (
              <m.div
                key={step.number}
                initial={{ y: reducedMotion ? 0 : 24 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={reducedMotion ? { duration: 0 } : { duration: 0.5, delay: index * 0.1 }}
                className="how-it-works-card"
              >
                <span className="how-it-works-number">{step.number}</span>
                <h3 className="how-it-works-step-title">{step.title}</h3>
                <p className="how-it-works-step-description">{step.description}</p>
              </m.div>
            ))}
          </div>
        </LazyMotion>
      </div>
    </section>
  );
};

export default HowItWorks;
